/**
 * SuperAdmin — API Cost Monitor
 * 4 sections: summary metric cards, conditional alert banner,
 * per-centre table with colour rules, service breakdown cards.
 *
 * Data: GET /api/superadmin/api-costs?month=YYYY-MM
 * Web only — never add to mobile RoleRouter (ADR-014: finance screens web-only).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiLoader, FiRefreshCw } from 'react-icons/fi';
import { getApiCosts } from '../../utils/api';
import colors from '../../styles/colors';

// Service config — keys match byService keys from the API response.
const SERVICES = [
  { key: 'sonnet',     label: 'Claude Sonnet 4.5', color: colors.brandPrimary, unitSuffix: 'K tokens'  },
  { key: 'haiku',      label: 'Claude Haiku 3.5',  color: '#1D9E75', unitSuffix: 'K tokens'  },
  { key: 'whisper',    label: 'Whisper STT',        color: '#534AB7', unitSuffix: 'minutes'   },
  { key: 'elevenLabs', label: 'ElevenLabs TTS',     color: '#D85A30', unitSuffix: 'K chars'   },
  { key: 'whatsapp',   label: 'WhatsApp msgs',      color: '#BA7517', unitSuffix: 'messages'  },
];

const PLAN_BADGE = {
  AI_ADMIN: { bg: '#E1F5EE', color: '#085041', label: 'AI Admin' },
  SCALE:    { bg: '#E1F5EE', color: '#085041', label: 'Scale'    },
  STARTER:  { bg: '#EEEDFE', color: '#3C3489', label: 'Starter'  },
  FREE:     { bg: '#F1EFE8', color: '#5F5E5A', label: 'Free'     },
};

// Inject responsive rules once — idempotent, same pattern as Overview.js.
const STYLE_ID = 'sa-costs-responsive';
if (!document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @media (max-width: 700px) { .sa-cost-metrics  { grid-template-columns: repeat(2, 1fr) !important; } }
    @media (max-width: 700px) { .sa-cost-services { grid-template-columns: repeat(2, 1fr) !important; } }
    .sa-cost-row:hover td { background: #fafafa !important; }
    @keyframes sa-cost-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(el);
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatInr(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function formatNum(n) {
  return Number(n || 0).toLocaleString('en-IN');
}

function generateMonths() {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return {
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('en-IN', { month: 'short', year: 'numeric' }),
    };
  });
}

// Colour rules per requirements spec.
// Free with any cost → red. AI Admin/Scale cost > 60% MRR → amber.
function costColour(centre) {
  if (centre.plan === 'FREE' && centre.costs.total > 0)                                    return '#A32D2D';
  if (['AI_ADMIN', 'SCALE'].includes(centre.plan) && centre.costAsMrrPercent > 60)         return '#854F0B';
  return '#1a1a2e';
}

function mrrPctDisplay(centre) {
  if (centre.costAsMrrPercent === null)  return { text: 'No rev', color: '#A32D2D' };
  if (centre.costAsMrrPercent > 60)      return { text: `${centre.costAsMrrPercent}%`, color: '#854F0B' };
  return { text: `${centre.costAsMrrPercent}%`, color: '#3B6D11' };
}

// ─── Styles ────────────────────────────────────────────────────────────────

const S = {
  header:      { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' },
  title:       { fontSize: '20px', fontWeight: '700', color: '#1a1a2e' },
  sub:         { fontSize: '13px', color: '#888', marginTop: '2px' },
  select:      { fontSize: '12px', padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: '6px', color: '#555', background: '#fff', cursor: 'pointer', minHeight: '36px' },

  metricsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' },
  mc:          { background: '#fff', borderRadius: '10px', padding: '16px', border: '1px solid #ebebeb' },
  mcLabel:     { fontSize: '11px', color: '#888', marginBottom: '6px' },
  mcValue:     (c) => ({ fontSize: '24px', fontWeight: '700', color: c || '#1a1a2e' }),
  mcSub:       (c) => ({ fontSize: '11px', color: c || '#888', marginTop: '4px' }),

  alertAmber:  { background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', color: '#633806', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' },
  alertRed:    { background: '#FDECEA', border: '1px solid #e74c3c', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', color: '#7a1515', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' },
  alertDot:    (red) => ({ width: '8px', height: '8px', borderRadius: '50%', background: red ? '#c0392b' : '#BA7517', flexShrink: 0 }),
  alertDismiss: { marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', minHeight: '32px', display: 'flex', alignItems: 'center' },

  card:        { background: '#fff', borderRadius: '10px', border: '1px solid #ebebeb', marginBottom: '20px', overflow: 'hidden' },
  cardHeader:  { padding: '14px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardTitle:   { fontSize: '14px', fontWeight: '600', color: '#1a1a2e' },
  cardNote:    { fontSize: '11px', color: '#888' },

  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:          { fontSize: '11px', fontWeight: '600', color: '#888', padding: '10px 14px', background: '#fafafa', borderBottom: '1px solid #ebebeb', whiteSpace: 'nowrap', textAlign: 'right' },
  thL:         { fontSize: '11px', fontWeight: '600', color: '#888', padding: '10px 14px', background: '#fafafa', borderBottom: '1px solid #ebebeb', whiteSpace: 'nowrap', textAlign: 'left' },
  td:          { padding: '11px 14px', borderBottom: '1px solid #f5f5f5', textAlign: 'right', verticalAlign: 'middle' },
  tdL:         { padding: '11px 14px', borderBottom: '1px solid #f5f5f5', textAlign: 'left',  verticalAlign: 'middle', fontWeight: '600' },

  barWrap:     { display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' },
  barBg:       { width: '60px', height: '4px', background: '#ebebeb', borderRadius: '2px', overflow: 'hidden', flexShrink: 0 },
  totalTd:     { padding: '11px 14px', textAlign: 'right', verticalAlign: 'middle', fontWeight: '600', background: '#fafafa' },

  insight:     { margin: '0 20px 16px', padding: '10px 14px', background: '#FAEEDA', borderRadius: '8px', fontSize: '12px', color: '#633806' },

  servicesGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', padding: '16px 20px' },
  bk:           { background: '#fafafa', borderRadius: '8px', padding: '14px', textAlign: 'center', border: '1px solid #ebebeb' },
  bkSvc:        { fontSize: '11px', color: '#888', marginBottom: '6px' },
  bkCost:       { fontSize: '18px', fontWeight: '700', color: '#1a1a2e' },
  bkUnit:       { fontSize: '10px', color: '#888', marginTop: '4px' },
  bkBar:        { height: '3px', background: '#ebebeb', borderRadius: '2px', marginTop: '10px', overflow: 'hidden' },
};

// ─── Loading / Error ────────────────────────────────────────────────────────

const LoadingView = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '10px', color: '#888' }}>
    <FiLoader style={{ fontSize: '20px', animation: 'sa-cost-spin 1s linear infinite' }} />
    <span style={{ fontSize: '13px' }}>Loading cost data…</span>
  </div>
);

const ErrorView = ({ message, onRetry }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '14px', textAlign: 'center', padding: '24px' }}>
    <div style={{ fontSize: '13px', color: '#e74c3c', maxWidth: '320px', lineHeight: '1.5' }}>{message}</div>
    <button type="button" onClick={onRetry} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '7px', border: 'none', background: '#1a6b5a', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}>
      <FiRefreshCw style={{ fontSize: '13px' }} /> Retry
    </button>
  </div>
);

// ─── Component ─────────────────────────────────────────────────────────────

const APICostMonitor = () => {
  const MONTHS = generateMonths();

  const [month,          setMonth]          = useState(MONTHS[0].value);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [data,           setData]           = useState(null);
  const [alertDismissed, setAlertDismissed] = useState(false);

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAlertDismissed(false);
    try {
      const res = await getApiCosts(month);
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load cost data. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchCosts(); }, [fetchCosts]);

  if (loading) return <LoadingView />;
  if (error)   return <ErrorView message={error} onRetry={fetchCosts} />;

  const { summary, byService, byCentre } = data;
  const monthLabel    = MONTHS.find(m => m.value === month)?.label || month;

  const highestSpender = byCentre[0] || null;
  const freeCostTotal  = byCentre.filter(c => c.plan === 'FREE').reduce((s, c) => s + c.costs.total, 0);
  const mrrTotal       = byCentre.reduce((s, c) => s + (c.mrr || 0), 0);
  const costPctOfMrr   = mrrTotal > 0 ? parseFloat((summary.totalCostInr / mrrTotal * 100).toFixed(1)) : null;
  const maxCost        = byCentre[0]?.costs.total || 1;
  const totalCost      = summary.totalCostInr;

  const pctColor = costPctOfMrr === null ? '#888' : costPctOfMrr > 15 ? '#A32D2D' : costPctOfMrr > 10 ? '#854F0B' : '#3B6D11';
  const pctSub   = costPctOfMrr === null ? 'No paying centres' : costPctOfMrr > 15 ? '⚠ Above 15%' : costPctOfMrr > 10 ? '⚠ Above target' : 'Target <10% ✓';

  const claude     = summary.claudeApiCostInr;
  const alertLevel = claude > 5000 ? 'red' : claude > 4000 ? 'amber' : null;
  const claudeColor = alertLevel === 'red' ? '#A32D2D' : alertLevel === 'amber' ? '#854F0B' : undefined;

  return (
    <div>

      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>API Cost Monitor</div>
          <div style={S.sub}>Per-centre token and API usage — identify cost vs revenue balance</div>
        </div>
        <select style={S.select} value={month} onChange={e => setMonth(e.target.value)} aria-label="Select month">
          {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>

      {/* Section 1 — Summary metric cards */}
      <div style={S.metricsGrid} className="sa-cost-metrics">
        <div style={S.mc}>
          <div style={S.mcLabel}>Total API cost</div>
          <div style={S.mcValue()}>{formatInr(summary.totalCostInr)}</div>
          <div style={S.mcSub()}>{monthLabel}</div>
        </div>
        <div style={S.mc}>
          <div style={S.mcLabel}>Cost % of MRR</div>
          <div style={S.mcValue(pctColor)}>{costPctOfMrr !== null ? `${costPctOfMrr}%` : '—'}</div>
          <div style={S.mcSub(costPctOfMrr > 10 ? pctColor : undefined)}>{pctSub}</div>
        </div>
        <div style={S.mc}>
          <div style={S.mcLabel}>Highest spender</div>
          <div style={{ ...S.mcValue(), fontSize: '16px', marginTop: '4px', lineHeight: '1.2' }}>
            {highestSpender?.tenantName || '—'}
          </div>
          <div style={S.mcSub()}>
            {highestSpender ? `${formatInr(highestSpender.costs.total)} this month` : 'No data'}
          </div>
        </div>
        <div style={S.mc}>
          <div style={S.mcLabel}>Claude API spend</div>
          <div style={S.mcValue(claudeColor)}>{formatInr(summary.claudeApiCostInr)}</div>
          <div style={S.mcSub(claudeColor)}>
            {alertLevel === 'red' ? '🔴 Threshold exceeded' : alertLevel === 'amber' ? '⚠ Alert at ₹5,000' : 'Alert at ₹5,000'}
          </div>
        </div>
      </div>

      {/* Section 2 — Alert banner (dismissible, resets on month change) */}
      {alertLevel && !alertDismissed && (
        <div style={alertLevel === 'red' ? S.alertRed : S.alertAmber}>
          <div style={S.alertDot(alertLevel === 'red')} />
          <span style={{ flex: 1 }}>
            Claude API spend is {formatInr(claude)} —{' '}
            {alertLevel === 'red' ? 'alert threshold exceeded.' : 'approaching ₹5,000 alert threshold.'}
            {highestSpender && ` Review ${highestSpender.tenantName} agent call frequency.`}
          </span>
          <button type="button" style={{ ...S.alertDismiss, color: alertLevel === 'red' ? '#7a1515' : '#854F0B' }} onClick={() => setAlertDismissed(true)} aria-label="Dismiss alert">
            <FiX style={{ fontSize: '14px' }} />
          </button>
        </div>
      )}

      {/* Section 3 — Per-centre table */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <span style={S.cardTitle}>Per-centre breakdown — {monthLabel}</span>
          <span style={S.cardNote}>Sorted by cost (highest first)</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.thL}>Centre</th>
                <th style={S.thL}>Plan</th>
                <th style={S.thL}>MRR</th>
                <th style={S.th}>Sonnet (K)</th>
                <th style={S.th}>Haiku (K)</th>
                <th style={S.th}>Whisper (min)</th>
                <th style={S.th}>11Labs (K)</th>
                <th style={S.th}>WA msgs</th>
                <th style={S.th}>Est. cost</th>
                <th style={S.th}>% MRR</th>
              </tr>
            </thead>
            <tbody>
              {byCentre.map(centre => {
                const cc    = costColour(centre);
                const mrrD  = mrrPctDisplay(centre);
                const badge = PLAN_BADGE[centre.plan] || PLAN_BADGE.FREE;
                const barW  = Math.round(centre.costs.total / maxCost * 100);
                const barColor = centre.plan === 'FREE' && centre.costs.total > 0 ? '#cccccc' : colors.brandPrimary;
                return (
                  <tr key={centre.tenantId} className="sa-cost-row">
                    <td style={S.tdL}>{centre.tenantName}</td>
                    <td style={{ ...S.td, textAlign: 'left' }}>
                      <span style={{ fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px', display: 'inline-block', background: badge.bg, color: badge.color }}>{badge.label}</span>
                    </td>
                    <td style={{ ...S.td, textAlign: 'left', color: centre.mrr === 0 ? '#888' : '#1a1a2e' }}>{formatInr(centre.mrr)}</td>
                    <td style={S.td}>{formatNum(centre.units.sonnetTokens1K)}</td>
                    <td style={S.td}>{formatNum(centre.units.haikuTokens1K)}</td>
                    <td style={S.td}>{formatNum(centre.units.whisperMinutes)}</td>
                    <td style={S.td}>{formatNum(centre.units.elevenLabsChars1K)}</td>
                    <td style={S.td}>{formatNum(centre.units.whatsappMessages)}</td>
                    <td style={S.td}>
                      <div style={S.barWrap}>
                        <div style={S.barBg}>
                          <div style={{ height: '4px', borderRadius: '2px', width: `${barW}%`, background: barColor }} />
                        </div>
                        <span style={{ color: cc, fontWeight: cc !== '#1a1a2e' ? '600' : '400' }}>{formatInr(centre.costs.total)}</span>
                      </div>
                    </td>
                    <td style={{ ...S.td, color: mrrD.color, fontWeight: '600' }}>{mrrD.text}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={8} style={{ ...S.totalTd, textAlign: 'right', color: '#888', fontSize: '11px', borderTop: '2px solid #ebebeb' }}>Platform total</td>
                <td style={{ ...S.totalTd, borderTop: '2px solid #ebebeb' }}>
                  <div style={S.barWrap}><span>{formatInr(summary.totalCostInr)}</span></div>
                </td>
                <td style={{ ...S.totalTd, borderTop: '2px solid #ebebeb' }} />
              </tr>
            </tfoot>
          </table>
        </div>
        {/* Insight row — only shown when free centres are consuming API cost */}
        {freeCostTotal > 0 && (
          <div style={S.insight}>
            Free centres consuming {formatInr(Math.round(freeCostTotal))}/month in API costs with no revenue. Consider plan-gating Claude AI Admin features to Starter+ plans.
          </div>
        )}
      </div>

      {/* Section 4 — Service breakdown cards */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <span style={S.cardTitle}>Platform cost breakdown by service</span>
          <span style={S.cardNote}>{monthLabel} — all centres combined</span>
        </div>
        <div style={S.servicesGrid} className="sa-cost-services">
          {SERVICES.map(({ key, label, color, unitSuffix }) => {
            const svc = byService[key] || { totalCostInr: 0, totalUnits: 0 };
            const pct = totalCost > 0 ? Math.round(svc.totalCostInr / totalCost * 100) : 0;
            return (
              <div key={key} style={S.bk}>
                <div style={S.bkSvc}>{label}</div>
                <div style={S.bkCost}>{formatInr(svc.totalCostInr)}</div>
                <div style={S.bkUnit}>{formatNum(svc.totalUnits)} {unitSuffix}</div>
                <div style={S.bkBar}>
                  <div style={{ height: '3px', borderRadius: '2px', width: `${pct}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default APICostMonitor;
